import React, { useState, useEffect } from 'react'
import { api } from '@/lib/api'
import { Select } from '@/components/ui/Select'
import { inputClass, inputStyle, labelClass, labelStyle } from '@/lib/styleKit'

interface SenderEmail {
  id: number
  email_address: string
  display_name?: string
  smtp_verified: boolean
}

interface SendEmailTestConfigProps {
  config: {
    send_to: 'self' | 'test_address' | 'real_contact'
    test_email: string
    sender_email_id: number | null
  }
  onChange: (config: any) => void
  /** Hide "Real contact" — set from the Playground, which already runs the
   *  whole simulation against one chosen contact. Offering "Real contact" as
   *  a Send To mode there risks a real email going out to a real person while
   *  someone is just clicking through a workflow simulation. */
  hideRealContact?: boolean
}

export const SendEmailTestConfig: React.FC<SendEmailTestConfigProps> = ({ config, onChange, hideRealContact }) => {
  const [senders, setSenders] = useState<SenderEmail[]>([])

  useEffect(() => {
    api.get('/sender-emails/').then(res => {
      const verified = (res.data || []).filter((s: SenderEmail) => s.smtp_verified)
      setSenders(verified)
      if (verified.length === 1 && !config.sender_email_id) {
        onChange({ ...config, sender_email_id: verified[0].id })
      }
    }).catch(() => {})
  }, [])

  return (
    <div className="space-y-3">
      <div>
        <label className={labelClass} style={labelStyle}>Send To</label>
        <div className="space-y-1.5">
          {[
            { value: 'self' as const, label: 'My email (self)' },
            { value: 'test_address' as const, label: 'Test email address' },
            ...(hideRealContact ? [] : [{ value: 'real_contact' as const, label: 'Real contact' }]),
          ].map(opt => (
            <label key={opt.value} className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="radio"
                name="send_to"
                value={opt.value}
                checked={config.send_to === opt.value}
                onChange={() => onChange({ ...config, send_to: opt.value })}
                className="text-primary"
              />
              <span className="text-foreground">{opt.label}</span>
            </label>
          ))}
        </div>
      </div>

      {config.send_to === 'test_address' && (
        <div>
          <label className={labelClass} style={labelStyle}>Test Email</label>
          <input
            type="email"
            value={config.test_email}
            onChange={e => onChange({ ...config, test_email: e.target.value })}
            placeholder="test@example.com"
            className={inputClass}
            style={inputStyle}
          />
        </div>
      )}

      <div>
        <label className={labelClass} style={labelStyle}>Send From</label>
        {senders.length === 0 ? (
          <p className="text-xs text-amber-600">No verified sender emails found</p>
        ) : (
          <Select
            value={config.sender_email_id ? String(config.sender_email_id) : ''}
            onChange={v => onChange({ ...config, sender_email_id: v ? Number(v) : null })}
            options={senders.map(s => ({
              value: String(s.id),
              label: s.display_name ? `${s.display_name} <${s.email_address}>` : s.email_address,
            }))}
            placeholder="Select sender..."
            ariaLabel="Send from"
            className="w-full"
          />
        )}
      </div>
    </div>
  )
}
